"use client";
import React, { useEffect, useState, useRef, Suspense, lazy } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Paper,
  Stack,
  RadioGroup,
  FormControlLabel,
  Radio,
  Collapse,
  Checkbox,
  Divider,
  Container,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Avatar,
  Skeleton,
} from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import {
  Description,
  Save,
  Print as PrintIcon,
  SubdirectoryArrowRight,
  AccountCircle,
  Update,
} from "@mui/icons-material";
import { useReactToPrint } from "react-to-print";

import { blobToBase64 } from "../../../../../../utils/blob";
import { QK } from "../../../../../../base/qk";
import {
  createAssessmentSubmission,
  getAssignedClientAssessments,
  getAssignedFormDetails,
  updateAssessmentSubmission,
  getAssessmentPrintData,
} from "../../../../../../api/services/Assessment/AssignAssessment";
import { getActiveCarePlans } from "../../../../../../api/services/carePathWay";

import { SignatureFieldBase64 } from "../../../../../../components/CustomForm";
import CustomInput from "../../../../../../components/CustomForm/CustomInput";
import CustomTextArea from "../../../../../../components/CustomForm/CustomTextArea";
import CustomDate from "../../../../../../components/CustomForm/CustomDate";
import ConfirmModal from "../../../../../ConfirmModal/ConfirmModal";
import useClientDetails from "../../../../../../hooks/useClientDetails";
import CustomSelect from "../../../../../FormFields/CustomSelect";
import AssessmentSkeleton from "./AssessmentSkeleton";
import NoDataMessage from "./NoDataMessage";

const PrintableAssessment = lazy(() => import("../Print/PrintableAssessment"));

const resolveSubmissionId = (data, existingId = null) => {
  if (!data) return existingId;
  return (
    data.submission_id ??
    data.submissionId ??
    data?.submission?.id ??
    existingId ??
    null
  );
};

const collectValidFieldKeys = (sections = []) => {
  const keys = new Set();
  const walk = (fields = []) => {
    fields.forEach((f) => {
      keys.add(`field_${f.id}`);
      if (Array.isArray(f.branches) && f.branches.length > 0) walk(f.branches);
    });
  };
  sections.forEach((s) => walk(s.section_fields || s.fields || []));
  return keys;
};

const filterResponsesBySections = (responses = {}, sections = []) => {
  const validKeys = collectValidFieldKeys(sections);
  const filtered = {};
  Object.keys(responses || {}).forEach((key) => {
    if (validKeys.has(key)) filtered[key] = responses[key];
  });
  return filtered;
};

const ClientAssignedAssessments = () => {
  const { clientID } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { fullName, birthDate, profilePhoto, ageDisplay } = useClientDetails(clientID);

  const formIdFromUrl = searchParams.get("form");
  const careplanFromUrl = searchParams.get("careplan");

  const [activeTab, setActiveTab] = useState(
    formIdFromUrl || (window.location.hash ? window.location.hash.substring(1) : null)
  );
  const [selectedCarePlan, setSelectedCarePlan] = useState(careplanFromUrl || "");
  const [responses, setResponses] = useState({});
  const [submissionId, setSubmissionId] = useState(null);
  const [confirmOpen, setOpenConfirm] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef(null);

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const handlePrintAction = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Assessment_${fullName}`,
  });

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    setSubmissionId(null);
    setResponses({});
    const params = { form: newValue };
    if (selectedCarePlan) params.careplan = selectedCarePlan;
    setSearchParams(params);
    window.location.hash = newValue;
  };

  const handleCarePlanChange = (e) => {
    const value = e.target.value;
    setSelectedCarePlan(value);
    setActiveTab(null);
    setSubmissionId(null);
    setResponses({});
    const params = {};
    if (value) params.careplan = value;
    setSearchParams(params);
  };

  const handleChange = (fieldKey, value) => {
    setResponses((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const handleSignatureChange = async (fieldId, blob) => {
    const key = `field_${fieldId}`;
    const base64 = blob ? await blobToBase64(blob) : null;
    handleChange(key, base64);
  };

  const renderFieldWithBranches = (field, level = 0) => {
    const fieldKey = `field_${field.id}`;

    const currentValue =
      responses[fieldKey] ??
      (field.field_type === "multiselect"
        ? []
        : field.field_type === "checkbox"
        ? false
        : "");

    const alternativeValue = field.branch_condition || "No";

    return (
      <React.Fragment key={field.id}>
        <Box sx={{ width: "100%", mb: 0.5 }}>
          <Box
            sx={{
              ml: level * 2.5,
              p: level > 0 ? 1.5 : 0.5,
              borderRadius: level > 0 ? "0 8px 8px 0" : "0px",
              bgcolor: level > 0 ? "#f8fafc" : "transparent",
              borderLeft: level > 0 ? "3px solid #6366f1" : "none",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={level === 0 ? 600 : 500}
              sx={{ mb: 0.5, color: "#334155", fontSize: level === 0 ? "0.85rem" : "0.8rem" }}
            >
              {field.label}
            </Typography>

            {field.branches?.length > 0 ? (
              <RadioGroup
                row
                value={currentValue}
                onChange={(e) => handleChange(fieldKey, e.target.value)}
                sx={{ gap: 1 }}
              >
                <FormControlLabel
                  value={field.main_answer_label || "Yes"}
                  control={<Radio size="small" />}
                  label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>{field.main_answer_label || "Yes"}</Typography>}
                />
                <FormControlLabel
                  value={alternativeValue}
                  control={<Radio size="small" />}
                  label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>{alternativeValue}</Typography>}
                />
              </RadioGroup>
            ) : (
              <Box sx={{ mt: 0.5 }}>
                {field.field_type === "singleline" && (
                  <CustomInput fullWidth size="small" value={currentValue} onChange={(e) => handleChange(fieldKey, e.target.value)} sx={{ "& .MuiInputBase-root": { fontSize: "0.8rem" } }} />
                )}
                {field.field_type === "multiline" && (
                  <CustomTextArea fullWidth rows={2} value={currentValue} onChange={(e) => handleChange(fieldKey, e.target.value)} sx={{ "& .MuiInputBase-root": { fontSize: "0.8rem" } }} />
                )}
                {field.field_type === "number" && (
                  <CustomInput fullWidth type="number" size="small" value={currentValue} onChange={(e) => handleChange(fieldKey, e.target.value)} sx={{ "& .MuiInputBase-root": { fontSize: "0.8rem" } }} />
                )}
                {field.field_type === "date" && (
                  <CustomDate value={currentValue} onChange={(val) => handleChange(fieldKey, val)} />
                )}
                {field.field_type === "checkbox" && (
                  <FormControlLabel
                    control={<Checkbox checked={!!currentValue} onChange={(e) => handleChange(fieldKey, e.target.checked)} size="small" />}
                    label={<Typography variant="body2" sx={{ fontSize: "0.8rem" }}>Select if applicable</Typography>}
                  />
                )}
                {field.field_type === "select" && (
                  <FormControl fullWidth size="small">
                    <Select value={currentValue} onChange={(e) => handleChange(fieldKey, e.target.value)} sx={{ fontSize: "0.8rem" }}>
                      {field.options?.map((opt, idx) => (
                        <MenuItem key={idx} value={opt.value} sx={{ fontSize: "0.8rem" }}>{opt.value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {field.field_type === "multiselect" && (
                  <FormControl fullWidth size="small">
                    <Select
                      multiple
                      value={Array.isArray(currentValue) ? currentValue : []}
                      onChange={(e) => handleChange(fieldKey, e.target.value)}
                      input={<OutlinedInput size="small" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((value) => <Chip key={value} label={value} size="small" sx={{ fontSize: "0.7rem" }} />)}
                        </Box>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      {field.options?.map((opt, idx) => (
                        <MenuItem key={idx} value={opt.value} sx={{ fontSize: "0.8rem" }}>{opt.value}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {field.field_type === "signature" && (
                  <SignatureFieldBase64 value={currentValue} onChange={(b) => handleSignatureChange(field.id, b)} />
                )}
              </Box>
            )}
          </Box>
        </Box>

        {field.branches?.map((branch) => (
          <Box key={branch.id} sx={{ width: "100%" }}>
            <Collapse in={currentValue === alternativeValue} timeout="auto" unmountOnExit>
              <Box sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: (level + 1) * 2.5, mb: 0.5 }}>
                  <SubdirectoryArrowRight sx={{ fontSize: 14, color: "#6366f1" }} />
                  <Typography variant="caption" sx={{ color: "#6366f1", fontWeight: 600, textTransform: "uppercase", fontSize: "0.6rem" }}>
                    Follow-up
                  </Typography>
                </Stack>
                {renderFieldWithBranches(branch, level + 1)}
              </Box>
            </Collapse>
          </Box>
        ))}
      </React.Fragment>
    );
  };

  const { data: activeCarePlans = [], isLoading: carePlansLoading } = useQuery(
    [QK.activeCarePlans, { client: clientID }],
    () => getActiveCarePlans({ client: clientID })
  );

  const { data: assignedForms = [], isLoading: formsLoading } = useQuery(
    [QK.assignedClientAssessments, clientID, selectedCarePlan],
    () => getAssignedClientAssessments(clientID, selectedCarePlan || null),
    { enabled: !!clientID }
  );

  const { data: formDetail, isLoading: loadingDetail } = useQuery(
    [QK.assignedClientAssessmentDetail, activeTab, clientID, selectedCarePlan],
    () => getAssignedFormDetails(clientID, activeTab, selectedCarePlan || null),
    { enabled: !!activeTab }
  );

  useEffect(() => {
    if (!activeTab && assignedForms.length > 0) {
      const firstId = String(assignedForms[0].form_id);
      setActiveTab(firstId);
      const params = { form: firstId };
      if (selectedCarePlan) params.careplan = selectedCarePlan;
      setSearchParams(params);
      window.location.hash = firstId;
    }
  }, [assignedForms, activeTab, setSearchParams, selectedCarePlan]);

  useEffect(() => {
    if (formDetail) {
      const sections = formDetail.sections || [];
      const filtered = filterResponsesBySections(formDetail.responses || {}, sections);
      setResponses(filtered);
      setSubmissionId(resolveSubmissionId(formDetail, null));
    }
  }, [formDetail]);

  const submitMutation = useMutation(
    (data) => {
      const sections = formDetail?.sections || [];
      const filtered = filterResponsesBySections(data, sections);
      return submissionId
        ? updateAssessmentSubmission({ id: submissionId, responses: filtered })
        : createAssessmentSubmission({ client: clientID, form: activeTab, responses: filtered });
    },
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries([QK.assignedClientAssessmentDetail]);
        const newSubmissionId = resolveSubmissionId(res, submissionId);
        if (newSubmissionId) setSubmissionId(newSubmissionId);
        enqueueSnackbar("Assessment saved successfully", { variant: "success" });
        setOpenConfirm(false);
      },
    }
  );

  const handlePrint = async () => {
    if (!submissionId) return enqueueSnackbar("Please save before printing", { variant: "info" });
    setIsPrinting(true);
    try {
      const data = await getAssessmentPrintData(submissionId);
      setPrintData(data);
      setTimeout(() => {
        handlePrintAction();
        setIsPrinting(false);
      }, 800);
    } catch (error) {
      setIsPrinting(false);
      enqueueSnackbar("Print failed", { variant: "error" });
    }
  };

  const carePlanOptions = [
    { value: "", label: "All Care Plans" },
    ...activeCarePlans.map((plan) => ({ value: plan.id, label: plan.form_title })),
  ];

  const isLoading = formsLoading || carePlansLoading;

  return (
    <Box sx={{ width: "100%", pb: 12, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Suspense fallback={null}>
        <PrintableAssessment ref={printRef} data={printData} />
      </Suspense>

      <Paper elevation={0} sx={{ width: "100%", py: 2, px: { xs: 2, md: 4 }, bgcolor: "white", color: "#0f172a", borderRadius: 0, borderBottom: "1px solid #e2e8f0" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {isLoading ? (
              <Skeleton variant="circular" width={40} height={40} />
            ) : (
              <Avatar
                src={profilePhoto}
                sx={{ width: 40, height: 40, bgcolor: "#10b981", fontSize: 18, fontWeight: 700 }}
              >
                {fullName?.charAt(0) || "C"}
              </Avatar>
            )}
            <Box>
              {isLoading ? (
                <Skeleton variant="text" width={150} height={24} />
              ) : (
                <Typography variant="subtitle1" fontWeight={700} color="#0f172a" sx={{ fontSize: "0.95rem" }}>
                  {fullName || "Client"}
                </Typography>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                {isLoading ? (
                  <Skeleton variant="text" width={60} height={16} />
                ) : (
                  <Typography variant="caption" color="#64748b" fontWeight={500} sx={{ fontSize: "0.7rem" }}>
                    {ageDisplay || "N/A"}
                  </Typography>
                )}
                {isLoading ? (
                  <Skeleton variant="text" width={80} height={16} />
                ) : (
                  <Typography variant="caption" color="#64748b" fontWeight={500} sx={{ fontSize: "0.7rem" }}>
                    DOB: {birthDate || "N/A"}
                  </Typography>
                )}
                {isLoading ? (
                  <Skeleton variant="text" width={50} height={16} />
                ) : (
                  <Typography variant="caption" color="#64748b" fontWeight={500} sx={{ fontSize: "0.7rem" }}>
                    ID: #{clientID}
                  </Typography>
                )}
                {isLoading ? (
                  <Skeleton variant="rectangular" width={80} height={16} sx={{ borderRadius: 12 }} />
                ) : (
                  <Typography variant="caption" sx={{ color: "#10b981", fontWeight: 600, bgcolor: "#ecfdf5", px: 1, py: 0.25, borderRadius: 12, fontSize: "0.6rem" }}>
                    {formDetail?.title || "Assessment"}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, bgcolor: "#f8fafc", px: 1, py: 0.4, borderRadius: 1.5 }}>
              <AccountCircle sx={{ fontSize: 14, color: "#64748b" }} />
              <Box>
                <Typography variant="caption" sx={{ display: "block", lineHeight: 1, fontWeight: 600, color: "#94a3b8", fontSize: "0.55rem" }}>COMPLETED</Typography>
                {isLoading ? (
                  <Skeleton variant="text" width={40} height={12} />
                ) : (
                  <Typography variant="caption" fontWeight={500} color="#0f172a" sx={{ fontSize: "0.65rem" }}>{formDetail?.completed_by || "N/A"}</Typography>
                )}
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, bgcolor: "#f8fafc", px: 1, py: 0.4, borderRadius: 1.5 }}>
              <Update sx={{ fontSize: 14, color: "#64748b" }} />
              <Box>
                <Typography variant="caption" sx={{ display: "block", lineHeight: 1, fontWeight: 600, color: "#94a3b8", fontSize: "0.55rem" }}>UPDATED</Typography>
                {isLoading ? (
                  <Skeleton variant="text" width={60} height={12} />
                ) : (
                  <Typography variant="caption" fontWeight={500} color="#0f172a" sx={{ fontSize: "0.65rem" }}>{formDetail?.updated_at ? new Date(formDetail.updated_at).toLocaleDateString("en-GB") : "N/A"}</Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ width: "100%", bgcolor: "#fff", borderBottom: 1, borderColor: "divider", position: "sticky", top: 0, zIndex: 10, px: { xs: 2, md: 4 } }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: 1, py: 1 }}>
          <Box sx={{ minWidth: 180 }}>
            {isLoading ? (
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
            ) : (
              <CustomSelect
                options={carePlanOptions}
                value={selectedCarePlan}
                onChange={handleCarePlanChange}
                placeholder="Select Care Plan"
                size="small"
                fullWidth
              />
            )}
          </Box>
          <Box sx={{ flex: 1, overflowX: "auto" }}>
            {isLoading ? (
              <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
            ) : assignedForms.length > 0 ? (
              <Tabs
                value={activeTab || false}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTab-root": {
                    fontWeight: 600,
                    textTransform: "none",
                    py: 0.8,
                    fontSize: "0.75rem",
                    minHeight: 36,
                  },
                }}
              >
                {assignedForms.map((f) => (
                  <Tab key={f.form_id} label={f.form_title} value={String(f.form_id)} />
                ))}
              </Tabs>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 2 }}>
        {isLoading ? (
          <AssessmentSkeleton />
        ) : assignedForms.length === 0 ? (
          <NoDataMessage selectedCarePlan={selectedCarePlan} />
        ) : loadingDetail ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <CircularProgress size={40} thickness={4} />
          </Box>
        ) : (
          formDetail && (
            <Box>
              {formDetail.sections?.map((section) => (
                <Paper key={section.id} elevation={0} sx={{ mb: 2, p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Description color="primary" sx={{ fontSize: 18 }} />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: "0.9rem" }}>{section.title}</Typography>
                  </Stack>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {(section.section_fields || section.fields)?.map((field) => renderFieldWithBranches(field))}
                  </Box>
                </Paper>
              ))}

              <Paper elevation={10} sx={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", p: 1, px: 3, borderRadius: 8, bgcolor: "rgba(255, 255, 255, 0.95)", backdropFilter: "blur(10px)", zIndex: 1000, border: "1px solid #e2e8f0", display: "flex", gap: 1.5, alignItems: "center" }}>
                <Button variant="text" startIcon={isPrinting ? <CircularProgress size={14} /> : <PrintIcon sx={{ fontSize: 18 }} />} onClick={handlePrint} disabled={!submissionId || isPrinting} sx={{ borderRadius: 50, fontWeight: 600, fontSize: "0.75rem", textTransform: "none" }}>Print</Button>
                <Divider orientation="vertical" flexItem sx={{ height: 18 }} />
                <Button variant="contained" startIcon={<Save sx={{ fontSize: 18 }} />} onClick={() => setOpenConfirm(true)} disabled={submitMutation.isLoading} sx={{ borderRadius: 50, px: 3, fontWeight: 600, bgcolor: "#0f172a", textTransform: "none", fontSize: "0.75rem" }}>{submissionId ? "Update" : "Save"}</Button>
              </Paper>
            </Box>
          )
        )}
      </Container>
      <ConfirmModal show={confirmOpen} handleClose={() => setOpenConfirm(false)} handleConfirm={() => submitMutation.mutate(responses)} />
    </Box>
  );
};

export default ClientAssignedAssessments;